<template>
  <div class="dc">
    <div class="dc-warn">
      <AppIcon name="alert-circle" />
      <span>This card deletes or takes away <b>{{ count }}</b> {{ count === 1 ? "thing" : "things" }}. Undo can put {{ count === 1 ? "it" : "them" }} back.</span>
    </div>
    <div v-if="!asking" class="dc-actions">
      <button type="button" class="dc-btn dc-btn--danger" :disabled="busy" @click="ask">Delete…</button>
      <button type="button" class="dc-btn" :disabled="busy" @click="$emit('cancel')">Cancel</button>
    </div>
    <div v-else class="dc-ask">
      <label class="dc-label" :for="inputId">Type {{ count }} to delete</label>
      <div class="dc-actions">
        <input :id="inputId" ref="input" v-model="typed" class="dc-input" inputmode="numeric" autocomplete="off" @keyup.enter="go" />
        <button type="button" class="dc-btn dc-btn--danger" :disabled="busy || !matches" @click="go">Delete</button>
        <button type="button" class="dc-btn" :disabled="busy" @click="back">Back</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The second check on a card that deletes or takes things away: the person types how many things
 * go. The server checks the number again (Assistant::execute), so this is the courtesy, not the
 * protection.
 */
let seq = 0;

export default {
  name: "DangerConfirm",
  props: {
    count: { type: Number, required: true },
    busy: { type: Boolean, default: false },
  },
  emits: ["confirm", "cancel"],
  data() {
    return { asking: false, typed: "", inputId: "dc-input-" + ++seq };
  },
  computed: {
    matches() { return this.typed.trim() === String(this.count); },
  },
  methods: {
    ask() {
      this.asking = true;
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    back() {
      this.asking = false;
      this.typed = "";
    },
    go() {
      if (this.matches && !this.busy) this.$emit("confirm", this.typed.trim());
    },
  },
};
</script>

<style scoped>
/* In the drawer's look (RightDrawer, ChangeSetCard): a tinted callout, pill buttons, a rounded number field. */
.dc { margin-top: var(--s-3); }
.dc-warn { display: flex; align-items: flex-start; gap: var(--s-2); padding: 10px 12px; border-radius: 12px; background: var(--danger-bg); color: var(--text-primary); font-size: var(--t-body-sm); line-height: 1.5; }
.dc-warn svg { width: 16px; height: 16px; flex: none; margin-top: 1px; color: var(--danger); }
.dc-ask { margin-top: var(--s-3); }
.dc-label { display: block; margin-bottom: var(--s-2); font-size: var(--t-caption); font-weight: 600; color: var(--text-secondary); }
.dc-actions { display: flex; flex-wrap: wrap; align-items: center; gap: var(--s-2); margin-top: var(--s-3); }
.dc-ask .dc-actions { margin-top: 0; }
.dc-input { width: 6rem; height: 34px; box-sizing: border-box; padding: 0 14px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-input); color: var(--text-primary); font: inherit; font-size: var(--t-body); font-variant-numeric: tabular-nums; }
.dc-input:focus { outline: none; border-color: var(--danger); box-shadow: 0 0 0 3px var(--danger-bg); }
.dc-btn { display: inline-flex; align-items: center; justify-content: center; min-height: 34px; padding: 6px 16px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-primary); color: var(--text-secondary); font: inherit; font-size: var(--t-body-sm); font-weight: 600; line-height: 1.3; cursor: pointer; }
.dc-btn:hover:not(:disabled) { border-color: var(--brand-orange); color: var(--text-primary); }
.dc-btn:disabled { opacity: 0.45; cursor: default; }
.dc-btn--danger, .dc-btn--danger:hover:not(:disabled) { background: var(--btn-danger-hover-bg); border-color: var(--btn-danger-hover-bg); color: var(--btn-danger-hover-text); }
</style>
